import _ from "lodash";

export const HEALTHSTREAM_LEARNING_VERSION = 2;
export const HCA_HEALTHSTREAM_ORG_ID = "75a99644-d769-11ec-8330-069463faa5ff";
export const HCA_HEALTHSTREAM_LEARNING_ORG_ID = "a86b970c-a5b3-da11-8139-000423acef71";
export const HEALTHSTREAM_COURSE_VISIBILITY_AMBIFI = "ambifi";
export const HEALTHSTREAM_COURSE_VISIBILITY_ALL = "all";
export const HEALTHSTREAM_COURSE_VISIBILITY_STORAGE_KEY = "ambifi.healthStream.courseVisibility";

export function createEmptyHealthStreamLearning(overrides = {}) {
	return _.assign({
		version: HEALTHSTREAM_LEARNING_VERSION,
		fetchedAt: "",
		assignedLearning: [],
		selfEnrolledLearning: [],
		selfEnrolledUnavailable: false,
		otherElectiveLearning: [],
		classRegistrations: [],
		warnings: [],
		loading: false,
		error: ""
	}, overrides);
}

function asArray(value) {
	return _.isArray(value) ? value : [];
}

function firstNonEmptyString(...values) {
	const match = values.find(value => (_.isString(value) || _.isNumber(value)) && `${value}`.trim() !== "");
	return match === undefined || match === null ? "" : `${match}`.trim();
}

export function normalizeHealthStreamCourseVisibility(value) {
	if (value === true || value === HEALTHSTREAM_COURSE_VISIBILITY_ALL) {
		return HEALTHSTREAM_COURSE_VISIBILITY_ALL;
	}
	return HEALTHSTREAM_COURSE_VISIBILITY_AMBIFI;
}

function getHealthStreamCourseVisibilityStorage(storage) {
	if (storage !== undefined) {
		return storage;
	}
	if (typeof window === "undefined") {
		return null;
	}
	try {
		return window.localStorage;
	} catch (error) {
		return null;
	}
}

export function loadHealthStreamCourseVisibility(storage) {
	const resolvedStorage = getHealthStreamCourseVisibilityStorage(storage);
	if (!resolvedStorage || typeof resolvedStorage.getItem !== "function") {
		return HEALTHSTREAM_COURSE_VISIBILITY_AMBIFI;
	}
	try {
		return normalizeHealthStreamCourseVisibility(
			resolvedStorage.getItem(HEALTHSTREAM_COURSE_VISIBILITY_STORAGE_KEY)
		);
	} catch (error) {
		return HEALTHSTREAM_COURSE_VISIBILITY_AMBIFI;
	}
}

export function saveHealthStreamCourseVisibility(value, storage) {
	const normalizedValue = normalizeHealthStreamCourseVisibility(value);
	const resolvedStorage = getHealthStreamCourseVisibilityStorage(storage);
	if (!resolvedStorage || typeof resolvedStorage.setItem !== "function") {
		return normalizedValue;
	}
	try {
		resolvedStorage.setItem(HEALTHSTREAM_COURSE_VISIBILITY_STORAGE_KEY, normalizedValue);
	} catch (error) {
		// A blocked or full browser store should not prevent changing this display preference.
	}
	return normalizedValue;
}

export function getHealthStreamCourseTypeLabel(course = {}) {
	const hasChildren = asArray(course.subCourseList).length > 0 || asArray(course.items).length > 0;
	if (hasChildren) {
		return "Curriculum";
	}

	const courseType = firstNonEmptyString(course.courseType, course.type);
	if (courseType.toLowerCase() === "curriculum") {
		return "Curriculum";
	}
	if (!courseType || courseType.toLowerCase() === "course") {
		return "Course";
	}
	return courseType;
}

export function getHealthStreamDisplayStatus(course = {}, fallback = "Not Started") {
	return firstNonEmptyString(course.courseStatus, course.status, course.courseInstanceStatus, fallback);
}

export function getHealthStreamStatusBucket(courseOrStatus = {}) {
	const status = _.isPlainObject(courseOrStatus)
		? firstNonEmptyString(courseOrStatus.courseStatus, courseOrStatus.status, courseOrStatus.courseInstanceStatus)
		: firstNonEmptyString(courseOrStatus);
	const normalizedStatus = status.toLowerCase().replace(/[\s_-]+/g, " ").trim();

	if (normalizedStatus === "completed" || normalizedStatus === "complete") {
		return "completed";
	}
	if (["started", "in progress", "failed"].includes(normalizedStatus)) {
		return "started";
	}
	if (["not started", "not yet started", "assigned"].includes(normalizedStatus)) {
		return "notStarted";
	}
	if (normalizedStatus === "deleted") {
		return "ignored";
	}
	return "unknown";
}

export function getHealthStreamStatusRollup(course = {}, visibilityMode = HEALTHSTREAM_COURSE_VISIBILITY_ALL) {
	const items = getHealthStreamCourseLaunchItems(course, visibilityMode);
	return items.reduce((rollup, item) => {
		const bucket = getHealthStreamStatusBucket(item);
		if (bucket === "ignored") {
			return rollup;
		}
		return _.assign({}, rollup, {
			[bucket]: rollup[bucket] + 1,
			total: rollup.total + 1
		});
	}, {
		notStarted: 0,
		started: 0,
		completed: 0,
		unknown: 0,
		total: 0
	});
}

export function normalizeHealthStreamCourseUrl(courseOrUrl = "") {
	const candidate = _.isPlainObject(courseOrUrl)
		? firstNonEmptyString(
			courseOrUrl.healthStreamCourseUrl,
			courseOrUrl.healthstreamCourseUrl,
			courseOrUrl.health_stream_course_url,
			courseOrUrl.courseUrl,
			courseOrUrl.courseURL,
			courseOrUrl.course_url
		)
		: firstNonEmptyString(courseOrUrl);
	if (!candidate) {
		return "";
	}

	try {
		const parsedUrl = new URL(candidate);
		const authorityMatch = candidate.match(/^https:\/\/([^/?#]+)/i);
		const queryKeys = Array.from(parsedUrl.searchParams.keys());
		const hasRequiredQuery = queryKeys.length === 2
			&& parsedUrl.searchParams.getAll("oid").length === 1
			&& parsedUrl.searchParams.getAll("quickLink").length === 1
			&& firstNonEmptyString(parsedUrl.searchParams.get("oid")).toLowerCase() === HCA_HEALTHSTREAM_LEARNING_ORG_ID
			&& firstNonEmptyString(parsedUrl.searchParams.get("quickLink")) !== "";
		if (parsedUrl.protocol !== "https:"
			|| parsedUrl.username
			|| parsedUrl.password
			|| !authorityMatch
			|| authorityMatch[1].toLowerCase() !== "www.healthstream.com"
			|| parsedUrl.pathname.toLowerCase() !== "/hlc/common/course/quicklinks.aspx"
			|| parsedUrl.hash
			|| !hasRequiredQuery) {
			return "";
		}
		return parsedUrl.href;
	} catch (error) {
		return "";
	}
}

export function getHealthStreamAvailabilityLabel(course = {}) {
	if (course.isAmbiFiCourse === false) {
		return "Not an AmbiFi course";
	}
	return firstNonEmptyString(course.mappingStatus).toLowerCase() === "sync-pending"
		? "Sync pending"
		: "";
}

export function getHealthStreamLoginIdFromUser(user = {}) {
	const id34 = firstNonEmptyString(user["custom:id34"], user.id34);
	if (id34) {
		return id34;
	}
	const upn = firstNonEmptyString(user["custom:upn"], user.upn);
	return upn ? upn.split("@")[0].trim() : "";
}

export function resolveCompletedActivityInstanceId(event = {}, selectedActivityInstanceId = "") {
	const completionEvent = _.isPlainObject(event) ? event : {};
	return firstNonEmptyString(
		_.get(completionEvent, ["activity", "instanceId"]),
		_.get(completionEvent, ["workflowLaunch", "activityInstanceId"]),
		selectedActivityInstanceId
	);
}

function normalizeMappingStatus(item, activityId, subCourseList) {
	const mappingStatus = firstNonEmptyString(item.mappingStatus).toLowerCase();
	if (["mapped", "partially-mapped", "unmapped", "sync-pending"].includes(mappingStatus)) {
		return mappingStatus;
	}

	if (subCourseList.length > 0) {
		const leafCourses = _.flatMap(subCourseList, flattenNormalizedHealthStreamLeafCourses);
		const mappedCount = leafCourses.filter(subCourse => subCourse.isLaunchable).length;
		if (mappedCount === 0) {
			return "unmapped";
		}
		if (mappedCount < leafCourses.length) {
			return "partially-mapped";
		}
		return "mapped";
	}

	return activityId ? "mapped" : "unmapped";
}

export function normalizeHealthStreamLaunchItem(item = {}, inherited = {}) {
	const rawSubCourses = asArray(item.subCourseList).length > 0
		? item.subCourseList
		: asArray(item.items);
	const courseId = firstNonEmptyString(item.courseId, item.id, inherited.courseId);
	const courseVersion = firstNonEmptyString(item.courseVersion, item.version, inherited.courseVersion);
	const courseName = firstNonEmptyString(item.courseName, item.name, inherited.courseName);
	const activityId = firstNonEmptyString(item.activityId, item.ambifiGuid, item.ambifi_guid);
	const courseInstanceId = firstNonEmptyString(item.courseInstanceId, inherited.courseInstanceId);
	const courseUrl = normalizeHealthStreamCourseUrl(item);
	const subCourseList = rawSubCourses.map(subCourse => normalizeHealthStreamLaunchItem(subCourse, {
		courseInstanceId,
		sourceType: item.sourceType || inherited.sourceType
	}));
	const descendantIsLaunchable = subCourseList.some(subCourse => subCourse.isAmbiFiLaunchable);
	const mappingStatus = normalizeMappingStatus(item, activityId, subCourseList);
	const isSyncPending = mappingStatus === "sync-pending";
	const isAmbiFiLaunchable = !isSyncPending && (
		(!!activityId && item.isLaunchable !== false && item.isAmbiFiLaunchable !== false)
		|| descendantIsLaunchable
	);
	const isHealthStreamLaunchable = !!courseUrl;
	const mappingReason = firstNonEmptyString(
		item.mappingReason,
		item.unavailableReason,
		mappingStatus === "partially-mapped"
			? "Some courses in this curriculum are not available in AmbiFi."
			: (!isAmbiFiLaunchable ? "No matching AmbiFi activity is available for this HealthStream course." : "")
	);

	return _.assign({}, item, {
		sourceType: firstNonEmptyString(item.sourceType, inherited.sourceType),
		enrollmentSource: firstNonEmptyString(item.enrollmentSource, inherited.enrollmentSource),
		courseId,
		courseVersion,
		courseInstanceId,
		courseName,
		courseNumber: firstNonEmptyString(item.courseNumber, item.number),
		courseType: firstNonEmptyString(item.courseType, item.type),
		courseStatus: firstNonEmptyString(item.courseStatus, item.status, item.courseInstanceStatus),
		courseDescription: firstNonEmptyString(item.courseDescription, item.course_description),
		learningObjectives: firstNonEmptyString(item.learningObjectives, item.learning_objectives),
		healthStreamCourseUrl: courseUrl,
		courseUrl,
		seatTimeInMinutes: item.seatTimeInMinutes === undefined || item.seatTimeInMinutes === null
			? null
			: item.seatTimeInMinutes,
		activityId,
		subCourseList,
		isMapped: !isSyncPending && (item.isMapped === true || ["mapped", "partially-mapped"].includes(mappingStatus)),
		isFullyMapped: !isSyncPending && (item.isFullyMapped === true || mappingStatus === "mapped"),
		// Keep the existing `isLaunchable` contract AmbiFi-specific. External
		// HealthStream navigation is deliberately represented separately so it
		// can never be mistaken for an AmbiFi activity launch or completion.
		isLaunchable: isAmbiFiLaunchable,
		isAmbiFiLaunchable,
		isHealthStreamLaunchable,
		mappingStatus,
		mappingReason
	});
}

function flattenNormalizedHealthStreamLeafCourses(course) {
	if (course.subCourseList.length === 0) {
		return [course];
	}
	return _.flatMap(course.subCourseList, flattenNormalizedHealthStreamLeafCourses);
}

export function getHealthStreamLeafCourses(course = {}) {
	return flattenNormalizedHealthStreamLeafCourses(normalizeHealthStreamLaunchItem(course));
}

export function getHealthStreamLaunchDestination(course = {}, inherited = {}) {
	const normalizedCourse = normalizeHealthStreamLaunchItem(course, inherited);
	if (normalizedCourse.activityId && normalizedCourse.isAmbiFiLaunchable) {
		return {
			type: "ambifi",
			course: normalizedCourse,
			courseUrl: ""
		};
	}
	if (normalizedCourse.isHealthStreamLaunchable) {
		return {
			type: "healthstream",
			course: normalizedCourse,
			courseUrl: normalizedCourse.healthStreamCourseUrl
		};
	}
	return {
		type: "unavailable",
		course: normalizedCourse,
		courseUrl: ""
	};
}

function normalizeAssignedLearning(assignments) {
	return asArray(assignments).map(assignment => _.assign({}, assignment, {
		assignments: asArray(assignment.assignments).map(course => normalizeHealthStreamLaunchItem(course, {
			sourceType: "assigned"
		}))
	}));
}

function normalizeElectiveLearning(courses, enrollmentSource) {
	return asArray(courses).map(course => normalizeHealthStreamLaunchItem(course, {
		sourceType: "elective",
		enrollmentSource
	}));
}

export function normalizeHealthStreamLearningResponse(response, overrides = {}) {
	if (_.isArray(response)) {
		return createEmptyHealthStreamLearning(_.assign({
			assignedLearning: normalizeAssignedLearning(response)
		}, overrides));
	}

	const body = _.isPlainObject(response) ? response : {};
	const assignedLearning = body.assignedLearning
		|| body.assignments
		|| body.assigned
		|| [];
	const selfEnrolledLearning = body.selfEnrolledLearning
		|| body.selfEnrolledCourses
		|| [];
	const otherElectiveLearning = body.otherElectiveLearning
		|| body.otherElectiveCourses
		|| [];

	return createEmptyHealthStreamLearning(_.assign({
		version: Number(body.version) || HEALTHSTREAM_LEARNING_VERSION,
		fetchedAt: firstNonEmptyString(body.fetchedAt),
		assignedLearning: normalizeAssignedLearning(assignedLearning),
		selfEnrolledLearning: normalizeElectiveLearning(selfEnrolledLearning, "learner-created"),
		selfEnrolledUnavailable: body.selfEnrolledUnavailable === true,
		otherElectiveLearning: normalizeElectiveLearning(otherElectiveLearning, "created-by-other"),
		classRegistrations: asArray(body.classRegistrations),
		warnings: asArray(body.warnings).filter(Boolean),
		loading: body.loading === true,
		error: firstNonEmptyString(body.error)
	}, overrides));
}

export function getHealthStreamCourseLaunchItems(course = {}, visibilityMode = HEALTHSTREAM_COURSE_VISIBILITY_AMBIFI) {
	const items = getHealthStreamLeafCourses(course);
	return normalizeHealthStreamCourseVisibility(visibilityMode) === HEALTHSTREAM_COURSE_VISIBILITY_ALL
		? items
		: items.filter(item => item.isAmbiFiLaunchable);
}

export function isHealthStreamCourseLaunchable(course = {}) {
	const normalizedCourse = normalizeHealthStreamLaunchItem(course);
	return normalizedCourse.isAmbiFiLaunchable
		|| normalizedCourse.isHealthStreamLaunchable
		|| flattenNormalizedHealthStreamLeafCourses(normalizedCourse).some(item => (
			item.isAmbiFiLaunchable || item.isHealthStreamLaunchable
		));
}

export function isHealthStreamCourseVisible(course = {}, visibilityMode = HEALTHSTREAM_COURSE_VISIBILITY_AMBIFI) {
	if (normalizeHealthStreamCourseVisibility(visibilityMode) === HEALTHSTREAM_COURSE_VISIBILITY_ALL) {
		return true;
	}
	return normalizeHealthStreamLaunchItem(course).isAmbiFiLaunchable;
}

export function getVisibleAssignedLearning(assignments = [], visibilityMode = HEALTHSTREAM_COURSE_VISIBILITY_AMBIFI) {
	return normalizeAssignedLearning(assignments).map(assignment => _.assign({}, assignment, {
		assignments: assignment.assignments.filter(course => isHealthStreamCourseVisible(course, visibilityMode))
	})).filter(assignment => assignment.assignments.length > 0);
}

export function getVisibleSelfEnrolledLearning(courses = [], visibilityMode = HEALTHSTREAM_COURSE_VISIBILITY_AMBIFI) {
	const normalizedCourses = normalizeElectiveLearning(courses, "learner-created");
	return normalizedCourses.filter(course => isHealthStreamCourseVisible(course, visibilityMode));
}

export function countVisibleHealthStreamLearning(assignedLearning = [], selfEnrolledLearning = [], visibilityMode = HEALTHSTREAM_COURSE_VISIBILITY_AMBIFI) {
	const assignedCourseCount = getVisibleAssignedLearning(assignedLearning, visibilityMode)
		.reduce((count, assignment) => count + assignment.assignments.length, 0);
	return assignedCourseCount + getVisibleSelfEnrolledLearning(selfEnrolledLearning, visibilityMode).length;
}

export function countLaunchableSelfEnrolledLearning(courses = []) {
	return normalizeElectiveLearning(courses, "learner-created")
		.filter(course => isHealthStreamCourseLaunchable(course)).length;
}

export function shouldForcePersistCompletedActivityForHealthStream(orgId, healthStreamContext = null, launch = {}) {
	const normalizedOrgId = firstNonEmptyString(orgId).toLowerCase();
	const hasExplicitHealthStreamCourse = _.isPlainObject(healthStreamContext)
		&& firstNonEmptyString(healthStreamContext.courseId) !== "";
	const launchDetails = _.isPlainObject(launch) ? launch : {};
	const isOrdinaryEditableLearnerLaunch = launchDetails.readOnly !== true
		&& launchDetails.collaborationLaunch !== true
		&& launchDetails.workflowLaunch !== true
		&& firstNonEmptyString(launchDetails.projectOpsLaunchIntent) === "";
	return normalizedOrgId === HCA_HEALTHSTREAM_ORG_ID
		&& !hasExplicitHealthStreamCourse
		&& isOrdinaryEditableLearnerLaunch;
}

export function buildHealthStreamLaunchContext(course = {}, parentCourse = {}, sourceType = "assigned") {
	const parent = normalizeHealthStreamLaunchItem(parentCourse);
	const launchCourse = normalizeHealthStreamLaunchItem(course, {
		courseInstanceId: parent.courseInstanceId,
		sourceType
	});
	const hasDistinctContainer = !!(parent.courseId && parent.courseId !== launchCourse.courseId);
	return {
		completionContractVersion: HEALTHSTREAM_LEARNING_VERSION,
		sourceType,
		courseId: launchCourse.courseId,
		courseVersion: launchCourse.courseVersion,
		courseName: launchCourse.courseName,
		courseInstanceId: launchCourse.courseInstanceId || parent.courseInstanceId || "",
		containerCourseId: hasDistinctContainer ? parent.courseId : "",
		containerCourseVersion: hasDistinctContainer ? parent.courseVersion : "",
		containerCourseName: hasDistinctContainer ? parent.courseName : ""
	};
}
